import { useState, type FC } from 'react';
import { Button, Badge, Spinner } from 'flowbite-react';
import { HiFilter, HiX, HiRefresh } from 'react-icons/hi';
import type {
  FilterCondition,
  FilterGroup,
  HeatmapSavedFilter,
  SystemPreset,
} from '../../types/filter';
import {
  countActiveConditions,
  isFilterCondition,
  isFilterGroup,
} from '../../types/filter';
import {
  getFilterFieldById,
  OPERATOR_LABELS,
} from '../../config/heatmap-filter-fields';
import { FilterBuilder } from './FilterBuilder';
import { SavedFiltersDropdown } from './SavedFiltersDropdown';
import { SaveFilterModal } from './SaveFilterModal';

interface FilterBarProps {
  activeFilter: FilterGroup | null;
  onFilterChange: (newFilter: FilterGroup | null) => void;
  systemPresets: SystemPreset[];
  userPresets: HeatmapSavedFilter[];
  activePresetId?: string | null;
  onSelectPreset: (preset: SystemPreset | HeatmapSavedFilter) => void;
  onSavePreset: (name: string, description: string, isDefault: boolean) => Promise<void>;
  onDeletePreset: (presetId: string) => void;
  onSetDefaultPreset: (presetId: string) => void;
  pointCount: number | null;
  isLoading?: boolean;
}

export const FilterBar: FC<FilterBarProps> = ({
  activeFilter,
  onFilterChange,
  systemPresets,
  userPresets,
  activePresetId,
  onSelectPreset,
  onSavePreset,
  onDeletePreset,
  onSetDefaultPreset,
  pointCount,
  isLoading = false,
}) => {
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  const activeCount = countActiveConditions(activeFilter);

  // Extract flat list of active leaf conditions for display chips
  const extractActiveConditions = (
    group: FilterGroup | null
  ): FilterCondition[] => {
    if (!group || !Array.isArray(group.children)) return [];
    const list: FilterCondition[] = [];

    const walk = (node: FilterGroup) => {
      for (const child of node.children) {
        if (isFilterGroup(child)) {
          walk(child);
        } else if (isFilterCondition(child)) {
          const noValOps = [
            'is_empty',
            'is_not_empty',
            'is_today',
            'is_past_7_days',
            'is_past_30_days',
          ];
          if (noValOps.includes(child.operator) || child.value !== null) {
            list.push(child);
          }
        }
      }
    };

    walk(group);
    return list;
  };

  const activeConditions = extractActiveConditions(activeFilter);

  // Remove a specific condition chip
  const handleRemoveCondition = (conditionId: string) => {
    if (!activeFilter) return;

    const removeRecursive = (group: FilterGroup): FilterGroup => {
      const filtered = group.children
        .filter((c) => c.id !== conditionId)
        .map((c) => (isFilterGroup(c) ? removeRecursive(c) : c));

      return {
        ...group,
        children: filtered,
      };
    };

    const updated = removeRecursive(activeFilter);
    const newCount = countActiveConditions(updated);
    onFilterChange(newCount > 0 ? updated : null);
  };

  const handleResetAll = () => {
    onFilterChange(null);
  };

  // Format a human-readable condition chip string
  const formatConditionChip = (cond: FilterCondition): string => {
    const fieldDef = getFilterFieldById(cond.field);
    const fieldLabel = fieldDef?.label || cond.field;
    const opLabel = OPERATOR_LABELS[cond.operator] || cond.operator;

    let valStr = '';
    if (cond.value !== null && cond.value !== undefined) {
      if (Array.isArray(cond.value)) {
        valStr =
          cond.value.length > 2
            ? `${cond.value.length} Werte`
            : cond.value.join(', ');
      } else if (typeof cond.value === 'object') {
        if ('min' in cond.value && 'max' in cond.value) {
          valStr = `${cond.value.min} - ${cond.value.max} J.`;
        } else if ('start' in cond.value && 'end' in cond.value) {
          valStr = `${cond.value.start} bis ${cond.value.end}`;
        }
      } else {
        valStr = String(cond.value);
      }
    }

    return valStr ? `${fieldLabel} ${opLabel} ${valStr}` : `${fieldLabel} ${opLabel}`;
  };

  return (
    <div className="space-y-2">
      {/* Primary Toolbar Row */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-wrap items-center gap-2">
          {/* Filter Modal Trigger Button */}
          <Button
            color={activeCount > 0 ? 'blue' : 'light'}
            size="xs"
            onClick={() => setIsBuilderOpen(true)}
            className="flex items-center gap-1.5 font-medium"
          >
            <HiFilter className="h-4 w-4" />
            <span>Filter</span>
            {activeCount > 0 && (
              <span className="ml-1 rounded-full bg-blue-700 px-1.5 py-0.5 text-[10px] text-white">
                {activeCount}
              </span>
            )}
          </Button>

          {/* Saved Presets Dropdown */}
          <SavedFiltersDropdown
            systemPresets={systemPresets}
            userPresets={userPresets}
            activePresetId={activePresetId}
            onSelectPreset={onSelectPreset}
            onSaveNewPreset={() => setIsSaveModalOpen(true)}
            onDeletePreset={onDeletePreset}
            onSetDefaultPreset={onSetDefaultPreset}
            onResetFilters={handleResetAll}
            disabled={isLoading}
          />

          {/* Reset button if filters are active */}
          {activeCount > 0 && (
            <Button
              color="gray"
              size="xs"
              onClick={handleResetAll}
              disabled={isLoading}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              title="Alle Filter entfernen"
            >
              <HiRefresh className="h-3.5 w-3.5" />
              <span>Zurücksetzen</span>
            </Button>
          )}
        </div>

        {/* Status Indicators: Location Count & Loading Spinner */}
        <div className="flex items-center gap-3">
          {isLoading && (
            <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400">
              <Spinner size="xs" />
              <span>Daten werden geladen...</span>
            </div>
          )}

          {pointCount !== null && (
            <Badge color="info" size="sm" className="text-xs font-medium">
              {pointCount.toLocaleString('de-DE')} Standorte
            </Badge>
          )}
        </div>
      </div>

      {/* Active Filter Chips Bar */}
      {activeConditions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-1 py-1">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Aktive Filter:
          </span>
          {activeConditions.map((cond) => (
            <span
              key={cond.id}
              className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
            >
              <span className="max-w-[240px] truncate">
                {formatConditionChip(cond)}
              </span>
              <button
                type="button"
                onClick={() => handleRemoveCondition(cond.id)}
                className="ml-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full hover:bg-blue-200 dark:hover:bg-blue-800"
                title="Diesen Filter entfernen"
              >
                <HiX className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Filter Builder Modal */}
      <FilterBuilder
        show={isBuilderOpen}
        onClose={() => setIsBuilderOpen(false)}
        initialFilter={activeFilter}
        onApply={onFilterChange}
        onOpenSavePreset={() => {
          setIsBuilderOpen(false);
          setIsSaveModalOpen(true);
        }}
        isLoading={isLoading}
      />

      {/* Save Filter Preset Modal */}
      <SaveFilterModal
        show={isSaveModalOpen}
        onClose={() => setIsSaveModalOpen(false)}
        onSave={onSavePreset}
      />
    </div>
  );
};
